import { css } from '@emotion/react';
import { createColorVariableMap, type ColorTokenMode } from 'themes/colorTokenRegistry';

export type ThemeName = 'Orange' | 'Blue' | 'Purple';

const commonColors = {
  errorColor: '#FF433D',
  successColor: '#00AA5A',
  infoColor: '#5F8EFC',
};

export const mljColors = {
  primaryColor: '#fa6400',
  ...commonColors,
};

export const themesColors = {
  Blue: {
    primaryColor: '#3366FF',
    ...commonColors,
  },
  Orange: {
    primaryColor: '#ee4e2d',
    ...commonColors,
  },
  Purple: {
    primaryColor: '#6954F0',
    ...commonColors,
  },
};

const themesVars: Record<ThemeName, ReturnType<typeof css>> = {
  Blue: css`
    --brand-primary: ${themesColors.Blue.primaryColor};
    --brand-primary-hover: #6690ff;
    --brand-primary-active: #254edb;
    --brand-primary-soft: #cbdbfd;
    --fett-colors-primary: ${themesColors.Blue.primaryColor}; // app 主色，跟antd组件保持一致
    --fett-colors-primary-focus: #254edb;
    --fett-colors-primary-hover: #6690ff;
    --fett-colors-primary-light: #cbdbfd;
    --fett-colors-second: #fff7f7; // 套餐倒计时弹窗背景色
    --fett-colors-price: #f1694f; // 价格字体色
  `,
  Orange: css`
    --brand-primary: ${themesColors.Orange.primaryColor};
    --brand-primary-hover: #f4845f;
    --brand-primary-active: #cc3020;
    --brand-primary-soft: #fed8c8;
    --fett-colors-primary: ${themesColors.Orange.primaryColor}; // app 主色，跟antd组件保持一致
    --fett-colors-primary-focus: #cc3020;
    --fett-colors-primary-hover: #f4845f;
    --fett-colors-primary-light: #fed8c8;
    --fett-colors-second: #fff7f7; // 套餐倒计时弹窗背景色
    --fett-colors-price: #f1694f; // 价格字体色
  `,
  Purple: css`
    --brand-primary: ${themesColors.Purple.primaryColor};
    --brand-primary-hover: #9581fc;
    --brand-primary-active: #3129a3;
    --brand-primary-soft: #f4f0ff;
    --fett-colors-primary: ${themesColors.Purple.primaryColor}; // app 主色，跟antd组件保持一致
    --fett-colors-primary-focus: #3129a3;
    --fett-colors-primary-hover: #9581fc;
    --fett-colors-primary-light: #f4f0ff;
    --fett-colors-second: #fff7f7; // 套餐倒计时弹窗背景色
    --fett-colors-price: #f1694f; // 价格字体色
  `,
};

const commonVars = css`
  --fett-app-primary-color: var(--ant-primary-color);
  --fett-free-trail-banner-primay-color: var(--fett-colors-primary);
  --fett-free-trail-banner-bg-color: var(--fett-colors-second);
`;

const diyVars = css`
  --fett-right-height-modal-scroll-body-margin: 4px;
`;

/* Dark-only value primitive shared by the chat window surfaces below.
   Emitted only in dark mode; light keeps each token's own concrete value. */
export const CHAT_DARK_SURFACE_TOKEN = '--fett-chat-dark-surface';

/* Dark-mode-only var() rewiring: in dark mode these tokens are emitted as a
   reference to another token instead of their registry value, so grouped
   surfaces track one source. Light mode always emits the registry value. */
export const darkModeColorAliases: Record<string, string> = {
  '--fett-chat-stats-header-bg': `var(${CHAT_DARK_SURFACE_TOKEN})`,
  '--fett-chat-head-bg': `var(${CHAT_DARK_SURFACE_TOKEN})`,
  '--fett-chat-window-bg': `var(${CHAT_DARK_SURFACE_TOKEN})`,
  '--fett-chat-editor-bg': `var(${CHAT_DARK_SURFACE_TOKEN})`,
  '--fett-conv-sider-head-bg': 'var(--fett-bg-elevated)',
};

const createColorVariableDeclarations = (mode: ColorTokenMode) =>
  Object.entries(createColorVariableMap(mode))
    .map(
      ([name, value]) =>
        `${name}: ${mode === 'dark' ? darkModeColorAliases[name] ?? value : value};`,
    )
    .join('\n');

const createModeVars = (mode: ColorTokenMode) => css`
  color-scheme: ${mode};
  ${createColorVariableDeclarations(mode)}
  ${mode === 'dark'
    ? `/* Dark-only shared surface value; its concrete color comes from the registry. */
  ${CHAT_DARK_SURFACE_TOKEN}: ${createColorVariableMap('dark')['--fett-chat-window-bg']};`
    : ''}

  /* This surface follows the active brand in light mode and stays neutral in dark mode. */
  --fett-sider-nav-bg: ${mode === 'light' ? 'var(--fett-app-primary-color)' : '#15181c'};

  /* App sidebar nav surface: follows the active brand in light, fixed neutral in dark.
     Brand-dependent light value keeps it outside the registry, like --fett-sider-nav-bg. */
  --fett-app-sider-nav-primary-bg: ${mode === 'light'
    ? 'var(--fett-app-primary-color)'
    : '#131720'};

  /* Legacy aliases remain consumers; their concrete colors come from the registry. */
  --theme-background-color: var(--fett-aide-background);
  --theme-remind-color: var(--fett-aide-remind);
  --theme-fill-1: var(--fett-fill-600);
  --theme-fill-2: var(--fett-fill-500);
  --theme-fill-3: var(--fett-fill-400);
  --theme-fill-4: var(--fett-fill-300);
  --theme-fill-5: var(--fett-fill-200);
  --theme-fill-6: var(--fett-fill-100);
  --theme-fill-7: var(--fett-fill-0);
  --theme-text-1: var(--fett-content-400);
  --theme-text-2: var(--fett-content-300);
  --theme-text-3: var(--fett-content-200);
  --theme-text-4: var(${mode === 'light' ? '--fett-content-200' : '--fett-content-100'});
  --theme-border-1: var(--fett-streak-white);
  --theme-border-2: var(--fett-streak-grey);
`;

export const createThemeVariableStyles = (
  theme: ThemeName,
  mode: ColorTokenMode,
  useMljTheme = false,
) => {
  const themeVars = themesVars[theme];
  const modeVars = createModeVars(mode);

  return useMljTheme
    ? css`
        :root {
          ${themeVars}
          ${modeVars}
          ${commonVars}
          ${diyVars}
          --fett-app-primary-color: ${mljColors.primaryColor}; // 马六甲专用
        }
      `
    : css`
        :root {
          ${themeVars}
          ${modeVars}
          ${commonVars}
          ${diyVars}
          --fett-app-primary-color: var(--fett-colors-primary);
        }
      `;
};
